import React, { useState, useEffect } from "react";
import { PasswordStrength } from "../components/PasswordField";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Flame, Mail, Lock, User, Phone, Clock, MapPin, Eye, EyeOff, LifeBuoy } from "lucide-react";

import { LOGO_ICON } from "../assets/brand";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", membership_status: "active" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [support, setSupport] = useState({ phone: "", email: "", hours: "" });
  const [dupError, setDupError] = useState("");

  useEffect(() => { api.get("/support-contact").then(({ data }) => setSupport(data)).catch(() => {}); }, []);

  const upd = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); if (dupError) setDupError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !agreed) {
      toast.error("Please agree to the Privacy Policy to create an account.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success("Welcome back!");
        navigate(u.role === "member" ? "/portal" : u.role === "team_leader" ? "/my-teams" : "/dashboard");
      } else {
        const u = await register({ ...form, privacy_consent: agreed });
        toast.success("Account created!");
        navigate(u.role === "member" ? "/portal" : u.role === "team_leader" ? "/my-teams" : "/dashboard");
      }
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      toast.error(msg);
      if (/account already exists/i.test(msg)) setDupError(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all pl-11 pr-4 py-3 text-base";

  return (
    <div className="min-h-[100dvh] grid lg:grid-cols-2" style={{ background: "var(--sj-bg)" }}>
      <div className="relative hidden lg:block sj-ring-panel">
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON} alt="Streams of Joy Erith London" data-testid="login-logo" className="h-16 w-16 rounded-2xl shadow-lg" />
            <div className="leading-tight">
              <div className="font-head font-extrabold text-lg">Streams of Joy</div>
              <div className="text-sm font-semibold uppercase tracking-[0.2em] text-white/80">Erith London</div>
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.3em] mb-3" style={{ color: "#F5C518" }}>El Roi</div>
            <h1 className="font-head text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight">
              The God That Sees Me
            </h1>
            <p className="mt-5 text-white/90 text-lg max-w-md font-semibold">
              What God cannot do does not exist!
            </p>

            <div className="mt-8 space-y-3 max-w-md">
              <p className="text-white/80 text-sm">
                An Expression Of{" "}
                <a href="https://www.instagram.com/streamsofjoyinternational" target="_blank" rel="noreferrer" data-testid="login-ig-soj" className="font-semibold text-white hover:underline" style={{ textUnderlineOffset: "3px" }}>@streamsofjoyinternational</a>
              </p>
              <p className="text-white/80 text-sm">
                Lead Pastor{" "}
                <a href="https://www.instagram.com/jerryuchechukwueze" target="_blank" rel="noreferrer" data-testid="login-ig-pastor" className="font-semibold text-white hover:underline" style={{ textUnderlineOffset: "3px" }}>@jerryuchechukwueze</a>
              </p>
              <div className="flex items-start gap-3 rounded-xl px-4 py-3" style={{ background: "rgba(255,255,255,0.08)" }}>
                <Clock className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#F5C518" }} />
                <div className="text-sm">
                  <div className="font-semibold text-white">Service Times</div>
                  <div className="text-white/80">Wednesday 7:00pm</div>
                  <div className="text-white/80">Sunday 10:00am &amp; 12:00pm</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#F5C518" }} />
                <p className="text-white/85 text-sm">5 Pier Road, Erith, London DA8 1TA</p>
              </div>
            </div>
          </div>
          <p className="text-white/70 text-sm">Streams of Joy Erith London · Church Management System</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-8 sm:p-10">
        <div className="w-full max-w-md sj-fade-up">
          {/* Mobile branded banner */}
          <div className="lg:hidden sj-hero mb-8">
            <div className="relative p-5 text-white">
              <div className="flex items-center gap-2.5">
                <img src={LOGO_ICON} alt="Streams of Joy Erith London" className="h-10 w-10 rounded-xl shadow" />
                <div className="leading-tight">
                  <div className="font-head font-extrabold text-sm">Streams of Joy</div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/80">Erith London</div>
                </div>
              </div>
              <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.25em]" style={{ color: "#F5C518" }}>El Roi</div>
              <div className="font-head text-2xl font-extrabold leading-tight">The God That Sees Me</div>
              <div className="mt-1 text-white/90 text-sm font-semibold">What God cannot do does not exist!</div>
            </div>
          </div>

          <h2 className="font-head text-3xl font-extrabold tracking-tight" style={{ color: "var(--sj-text)" }}>
            {mode === "login" ? "Welcome back" : "Join the family"}
          </h2>
          <p className="mt-2 mb-8" style={{ color: "var(--sj-text-2)" }}>
            {mode === "login" ? "Sign in to your account to continue." : "Create your member account to get started."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
                  <input data-testid="register-name-input" required placeholder="Full name" value={form.name} onChange={upd("name")} className={inputCls} />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
                  <input data-testid="register-phone-input" type="tel" inputMode="tel" autoComplete="tel" placeholder="Phone (+44...)" value={form.phone} onChange={upd("phone")} className={inputCls} />
                </div>
              </>
            )}
            <div className="relative">
              {mode === "login"
                ? <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
                : <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-text-3)" }} />}
              <input data-testid="login-email-input" required type={mode === "login" ? "text" : "email"} inputMode={mode === "login" ? "tel" : "email"} autoComplete="username" autoCapitalize="none" spellCheck="false" placeholder={mode === "login" ? "Telephone number (or email)" : "Email address"} value={form.email} onChange={upd("email")} className={inputCls} />
            </div>
            {mode === "login" && (
              <p className="-mt-2 text-xs" style={{ color: "var(--sj-text-3)" }}>Sign in with your telephone number. Members with an email can still use that.</p>
            )}
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
              <input data-testid="login-password-input" required type={showPassword ? "text" : "password"} autoComplete={mode === "login" ? "current-password" : "new-password"} placeholder="Password" value={form.password} onChange={upd("password")} className={`${inputCls} pr-11`} />
              <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-black/5 transition-colors" style={{ color: "var(--sj-text-3)" }}>
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {mode !== "login" && <PasswordStrength value={form.password} testid="register-strength" />}

            {mode === "login" && (
              <div className="text-right -mt-1">
                <Link data-testid="forgot-password-link" to="/forgot-password" className="text-sm font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>
                  Forgot password?
                </Link>
              </div>
            )}

            {mode === "register" && (
              <label className="flex items-start gap-2.5 text-sm cursor-pointer" style={{ color: "var(--sj-text-2)" }}>
                <input
                  data-testid="register-privacy-checkbox"
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded accent-[#0B5E4E] shrink-0"
                />
                <span>
                  <span style={{ color: "#C0392B" }}>*</span> I agree to the{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" data-testid="register-privacy-link" className="font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>
                    Privacy &amp; Data Protection Policy
                  </a>.
                </span>
              </label>
            )}

            {dupError && (
              <div data-testid="register-duplicate-error" className="rounded-xl px-4 py-3 text-sm" style={{ background: "#FDE7E7", color: "#B91C1C", border: "1px solid #F5C2C2" }}>
                {dupError}
                <div className="mt-2 flex gap-3">
                  <Link data-testid="duplicate-reset-link" to="/forgot-password" className="font-semibold underline" style={{ color: "#B91C1C" }}>Reset password</Link>
                  <button type="button" data-testid="duplicate-signin-link" onClick={() => { setMode("login"); setDupError(""); }} className="font-semibold underline" style={{ color: "#B91C1C" }}>Sign in instead</button>
                </div>
              </div>
            )}

            <button
              data-testid="login-submit-button"
              disabled={loading || (mode === "register" && !agreed)}
              className="w-full rounded-full py-3 font-semibold text-white shadow-sm transition-all hover:brightness-105 disabled:opacity-60"
              style={{ background: "var(--sj-primary)" }}
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm" style={{ color: "var(--sj-text-2)" }}>
            {mode === "login" ? "Existing member?" : "Already have an account?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setDupError(""); }}
              className="font-semibold hover:underline"
              style={{ color: "var(--sj-primary)" }}
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>

          {mode === "login" && (
            <div data-testid="login-help-text" className="mt-5 rounded-xl border p-4" style={{ borderColor: "var(--sj-border)", background: "var(--sj-peach)" }}>
              <div className="flex items-center gap-2 font-semibold text-sm" style={{ color: "var(--sj-text)" }}>
                <LifeBuoy className="h-4 w-4" style={{ color: "var(--sj-primary)" }} /> Members Portal Support
              </div>
              <p className="text-xs mt-1" style={{ color: "var(--sj-text-2)" }}>
                {support.phone || support.email
                  ? "Trouble signing in? Contact the church office:"
                  : "Trouble signing in? Please contact the church office for help."}
              </p>
              {(support.phone || support.email) && (
                <div className="mt-2 space-y-1 text-sm">
                  {support.phone && <a href={`tel:${support.phone.replace(/\s/g, "")}`} data-testid="support-phone" className="flex items-center gap-2 font-medium hover:underline" style={{ color: "var(--sj-primary)" }}><Phone className="h-4 w-4" /> {support.phone}</a>}
                  {support.email && <a href={`mailto:${support.email}`} data-testid="support-email" className="flex items-center gap-2 font-medium hover:underline" style={{ color: "var(--sj-primary)" }}><Mail className="h-4 w-4" /> {support.email}</a>}
                  {support.hours && <p className="flex items-center gap-2 text-xs" style={{ color: "var(--sj-text-3)" }}><Clock className="h-4 w-4" /> {support.hours}</p>}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 text-center text-sm rounded-xl py-3 px-4" style={{ background: "var(--sj-peach)", color: "var(--sj-text-2)" }}>
            First timer / Visitor?{" "}
            <a href="/first-timers" data-testid="login-first-timers-link" className="font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>
              Connect with us
            </a>
          </div>

          <div className="mt-3 text-center text-sm rounded-xl py-3 px-4 border" style={{ borderColor: "var(--sj-border)", background: "#fff", color: "var(--sj-text-2)" }}>
            Evangelism / Outreach?{" "}
            <a href="/connect" data-testid="login-connect-link" className="font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>
              Connect with us
            </a>
          </div>

          <div className="mt-3 text-center text-sm rounded-xl py-3 px-4 border" style={{ borderColor: "var(--sj-border)", background: "#fff", color: "var(--sj-text-2)" }}>
            Joining a cell group?{" "}
            <Link to="/cell-response" data-testid="login-cell-form-link" className="font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>
              Complete the response form
            </Link>
          </div>

          <div className="mt-8 text-center text-xs" style={{ color: "var(--sj-text-3)" }}>
            <a href="/privacy" data-testid="login-privacy-link" className="hover:underline" style={{ color: "var(--sj-text-2)" }}>
              Privacy &amp; Data Protection Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

